import {ArrowRight, Search, MapPin, TrendingUp, Users, Briefcase} from 'lucide-react';
import { Link } from "react-router-dom";
import { mineralCategories, jobs } from "@/data/jobs";
import { useSeoMeta } from '@unhead/react';

const heroImage = "https://static.lumi.new/7b/7ba36efec5b41c7f9e301a3361a3b12e.png";

const stats = [
  { value: "8,400+", label: "Active Jobs", icon: Briefcase },
  { value: "340+", label: "Mine Sites", icon: MapPin },
  { value: "$112K", label: "Avg. Salary", icon: TrendingUp },
  { value: "24K+", label: "Job Seekers", icon: Users },
];

const featuredJobs = jobs.filter((j) => j.featured).slice(0, 3);

export default function Home() {
  useSeoMeta({
    title: 'FIFO Mine Jobs & Mineral Careers | MineralJobs',
    description: "Australia's #1 mining job board. Find FIFO, entry level & mineral mining jobs in Gold, Copper, Coal & Iron Ore.",
    ogTitle: 'FIFO Mine Jobs & Mineral Careers | MineralJobs',
    ogDescription: "Australia's #1 mining job board for FIFO and mineral careers.",
    ogImage: 'https://www.mineraljobs.com/og-home.jpg',
    ogUrl: 'https://www.mineraljobs.com/',
    twitterCard: 'summary_large_image',
  });
  return (
    <main>
    <div className="min-h-screen" style={{ background: "#111827" }}>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img src={heroImage} alt="Mining operation at golden hour" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(17,24,39,0.7) 50%, rgba(17,24,39,0.9) 100%)" }}></div>
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at bottom, rgba(249,115,22,0.08) 0%, transparent 60%)" }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20">
          {/* Bento Grid Hero Layout */}
          <div className="grid lg:grid-cols-12 gap-4 items-start">
            {/* Main headline - spans 7 cols */}
            <div className="lg:col-span-7 animate-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border mb-6 text-xs" style={{ borderColor: "rgba(249,115,22,0.3)", background: "rgba(249,115,22,0.08)", color: "#fb923c", fontFamily: "JetBrains Mono, monospace" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse"></span>
                {jobs.length} NEW POSITIONS THIS WEEK
              </div>
              <h1 className="text-5xl sm:text-7xl font-black text-white leading-tight mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
                Find Your
                <br />
                <span style={{ color: "#f97316" }}>Mining Career</span>
                <br />
                <span className="text-gray-400 text-3xl sm:text-4xl font-semibold">FIFO · Entry Level · Pro</span>
              </h1>
              <p className="text-lg text-gray-400 max-w-xl mb-8">
                Australia's most trusted job board for the mining industry. From <strong className="text-white">FIFO mine jobs</strong> to <strong className="text-white">entry level mining roles</strong> — your mineral career starts here.
              </p>
              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row gap-2 max-w-xl">
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Job title, mineral, location..."
                    className="w-full pl-10 pr-4 py-3.5 rounded-xl text-white placeholder-gray-500 outline-none border border-white/6 text-sm focus:border-orange-500/40 transition-colors"
                    style={{ background: "rgba(26,31,46,0.9)" }}
                  />
                </div>
                <Link
                  to="/#jobs"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all hover:scale-105 whitespace-nowrap"
                  style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}
                >
                  Search Jobs <ArrowRight size={16} />
                </Link>
              </div>
              {/* Popular searches */}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="text-xs text-gray-500">Popular:</span>
                {["FIFO Mine Jobs", "Entry Level Mining", "Gold Jobs WA", "Trainee Miner"].map((tag) => (
                  <button key={tag} className="text-xs px-3 py-1 rounded-full border transition-colors hover:bg-white/5" style={{ borderColor: "rgba(255,255,255,0.1)", color: "#8896b0" }}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats bento - right side */}
            <div className="lg:col-span-5 grid grid-cols-2 gap-3">
              {stats.map((s, i) => (
                <div key={s.label} className="bento-card p-5" style={{ animationDelay: `${i * 0.1}s` }}>
                  <s.icon size={18} style={{ color: "#f97316" }} className="mb-3" />
                  <p className="text-2xl font-black text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.value}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                </div>
              ))}
              {/* Entry Level CTA */}
              <div className="col-span-2 bento-card p-5 flex items-center justify-between" style={{ background: "linear-gradient(135deg, rgba(249,115,22,0.15), rgba(234,88,12,0.05))", borderColor: "rgba(249,115,22,0.2)" }}>
                <div>
                  <p className="text-white font-bold text-sm mb-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>No Experience? No Problem.</p>
                  <p className="text-gray-400 text-xs">1,200+ trainee & entry roles available</p>
                </div>
                <Link
                  to="/entry-level"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all hover:scale-105"
                  style={{ background: "#f97316", color: "white" }}
                >
                  Start Here <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-40">
          <div className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-white rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Jobs by Mineral Section */}
      <section className="py-16 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// BROWSE BY MINERAL</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8" style={{ fontFamily: "Outfit, sans-serif" }}>
          Jobs by Mineral
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {mineralCategories.map((m) => (
            <Link
              key={m.name}
              to={`/jobs/${m.name.toLowerCase().replace(" ", "-")}`}
              className="bento-card p-6 flex flex-col gap-3 group"
              style={{ borderColor: m.borderColor }}
            >
              <div className="text-3xl">{m.icon}</div>
              <div>
                <h3 className="font-bold text-white text-lg group-hover:text-orange-400 transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>{m.name}</h3>
                <p className="text-xs mt-0.5" style={{ color: m.color, fontFamily: "JetBrains Mono, monospace" }}>{m.count} active jobs</p>
              </div>
              <p className="text-xs text-gray-500">Avg: <span style={{ color: m.color }}>{m.avgSalary}</span></p>
              <span className="text-xs mt-auto self-start" style={{ color: "#f97316" }}>Browse {m.name} Jobs →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Jobs Bento */}
      <section className="py-8 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-sm font-medium block mb-1" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// FEATURED</span>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Hot Positions This Week</h2>
          </div>
          <Link to="/#jobs" className="text-sm flex items-center gap-1 hover:text-orange-400 transition-colors" style={{ color: "#8896b0" }}>
            All jobs <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          {featuredJobs.map((job, i) => (
            <div
              key={job.id}
              className="bento-card p-6 flex flex-col gap-3 orange-glow"
              style={{ borderColor: "rgba(249,115,22,0.2)", ...(i === 0 ? { gridRow: "span 1" } : {}) }}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: "rgba(249,115,22,0.15)", color: "#fb923c", fontFamily: "JetBrains Mono, monospace" }}>⭐ FEATURED</span>
                <span className="text-xs text-gray-500">{job.posted}</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: "#2e3850", color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>
                  {job.logo}
                </div>
                <div>
                  <h3 className="font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>{job.title}</h3>
                  <p className="text-gray-400 text-sm">{job.company}</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">{job.description}</p>
              <div className="flex items-center gap-2 flex-wrap">
                {job.tags.slice(0, 3).map((t) => (
                  <span key={t} className="text-xs px-2 py-0.5 rounded-md" style={{ background: "#2e3850", color: "#8896b0" }}>{t}</span>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div>
                  <span className="text-sm font-bold" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>{job.salary}</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin size={11} className="text-gray-500" />
                    <span className="text-xs text-gray-500">{job.location}</span>
                  </div>
                </div>
                <button className="px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:scale-105" style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}>
                  Apply
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Banner — Entry Level */}
      <section className="py-12 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="bento-card p-8 sm:p-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #2e1a0e, #1a1f2e)", borderColor: "rgba(249,115,22,0.2)" }}>
          <div className="absolute right-0 top-0 w-64 h-64 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #f97316, transparent)", transform: "translate(30%, -30%)" }}></div>
          <div className="relative z-10 max-w-2xl">
            <span className="text-xs mb-3 block" style={{ color: "#f97316", fontFamily: "JetBrains Mono, monospace" }}>// NEW TO MINING?</span>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
              Entry Level Mining Jobs — <span style={{ color: "#f97316" }}>No Experience Required</span>
            </h2>
            <p className="text-gray-400 mb-6">
              MineralJobs specialises in <strong className="text-white">inexperienced mining roles</strong>. Haul truck operators, site labourers, and trainees — we connect first-timers with mine sites that invest in training.
            </p>
            <Link
              to="/entry-level"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #f97316, #ea580c)", color: "white" }}
            >
              Explore Entry Level Roles <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </main>
  );
}
